import { useCallback, useEffect, useState } from "react";
import ApiCall from "../auth/ApiCall";

export interface DeviceInfo {
  id: number;
  platform: string;
  device_name: string;
  created_at: string;
  last_seen_at: string;
  is_current: boolean;
}

interface DevicesResponse {
  max_devices?: number;
  devices?: DeviceInfo[];
  error?: string;
}

interface UseDevicesReturn {
  devices: DeviceInfo[];
  maxDevices: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDevices(): UseDevicesReturn {
  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [maxDevices, setMaxDevices] = useState<number>(5);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDevices = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const response = (await ApiCall.makeAuthenticatedRequest(
      "/api/user/devices/",
    )) as DevicesResponse;

    if (response.error) {
      setError(response.error);
    } else {
      setDevices(response.devices || []);
      setMaxDevices(response.max_devices ?? 5);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  return { devices, maxDevices, isLoading, error, refetch: fetchDevices };
}

export async function revokeDevice(
  deviceId: number,
): Promise<{ error?: string }> {
  const response = await ApiCall.makeAuthenticatedRequest(
    `/api/user/devices/${deviceId}/revoke/`,
    { method: "POST" },
  );
  return response.error ? { error: String(response.error) } : {};
}

// Frees this device's slot and removes its push tokens. Must be called
// before Firebase signOut() while the ID token is still valid.
export async function logoutCurrentDevice(): Promise<void> {
  try {
    await ApiCall.makeAuthenticatedRequest("/api/user/devices/logout/", {
      method: "POST",
    });
  } catch {
    // best-effort — sign-out proceeds regardless
  }
}
